import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import heroOwner from "/wp-content/uploads/youware-assets/hero-frustrated-business.webp";
import heroDash from "/wp-content/uploads/youware-assets/hero-ai-dashboard.webp";
import logoEclipseSwan from "/wp-content/uploads/youware-assets/logo-eclipse-swan.webp";
import audioDemo from "/wp-content/uploads/youware-assets/audio-demo.mp3";
import { HamburgerButton } from "./components/HamburgerButton";
import { LegalLinks } from "./components/footer/LegalLinks";

type ResponseTimeKey = "Within 5 Minutes" | "30 Minutes" | "1 Hour" | "24+ Hours";

type ProblemStat = {
  label: string;
  value: number;
  suffix?: string;
  description: string;
  sourceLabel: string;
  sourceUrl: string;
  footnote: string;
};

type FeatureTile = {
  icon: string;
  title: string;
  bullets: string[];
};

type UseCase = {
  title: string;
  narrative: string;
  keyword: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

type ComparisonRow = {
  feature: string;
  youware: string;
  human: string;
  voicemail: string;
};

type RoiInputs = {
  adSpend: string;
  leads: string;
  saleValue: string;
  responseTime: ResponseTimeKey;
};

type Testimonial = {
  name: string;
  role: string;
  quote: string;
  image: string;
};

type WorkflowStep = {
  icon: string;
  title: string;
  bullets: string[];
};

type Metric = {
  value: number;
  suffix?: string;
  caption: string;
  sourceLabel: string;
  sourceUrl: string;
  footnote: string;
};

type ChatMessage = {
  from: "user" | "ai";
  text: string;
  timestamp: string;
};

const problemStats: ProblemStat[] = [ // citations from HBR², Forbes³, InsideSales⁴, Velocify¹
  {
    label: "78% Buy From First Responder",
    value: 78,
    suffix: "%",
    description:
      "Velocity wins the deal: 78% of buyers choose the company that replies first.",
    sourceLabel: "Vendasta",
    sourceUrl: "https://www.vendasta.com/blog/lead-response-time/?utm_source=chatgpt.com",
    footnote: "¹",
  },
  {
    label: "47 Hours Average Response Time",
    value: 47,
    suffix: " hrs",
    description:
      "Across industries, the average response time, if any at all, is 47 hours—before a lead hears back after leaving a voicemail.",
    sourceLabel: "leadport.app",
    sourceUrl:
      "https://www.leadport.app/en/blog/key-lead-response-time-statistics/?utm_source=chatgpt.com",
    footnote: "³",
  },
  {
    label: "73% of Leads Never Contacted",
    value: 73,
    suffix: "%",
    description:
      "Most businesses never follow up at all—73% of collected leads never receive a call or email.",
    sourceLabel: "CallTools",
    sourceUrl:
      "https://calltools.com/blog/6-strategies-to-improve-speed-to-lead-conversion-time/?utm_source=chatgpt.com",
    footnote: "⁴",
  },
  {
    label: "391% Higher Conversion Rates",
    value: 391,
    suffix: "%",
    description:
      "Responding within one minute instead of waiting five minutes yields a 391% lift in conversions, so every inbound call lead stays engaged.",
    sourceLabel: "Lead Angel",
    sourceUrl: "https://www.leadangel.com/blog/operations/speed-to-lead-statistics/",
    footnote: "²",
  },
];

const featureGrid: FeatureTile[] = [
  {
    icon: "⚡",
    title: "Answers in 3 Rings or Less",
    bullets: [
      "Never miss a call triggered by a Google, Facebook, or LinkedIn ad.",
      "24/7 availability, even after hours and weekends.",
      "Captures leads while competitors' lines go to voicemail.",
    ],
  },
  {
    icon: "📅",
    title: "Automated Appointment Scheduling",
    bullets: [
      "Books meetings straight into your calendar without staff.",
      "Sends confirmation texts and reminders to keep no-shows low.",
      "Eliminates back-and-forth and keeps inbound call leads engaged.",
    ],
  },
  {
    icon: "🎯",
    title: "Intelligent Lead Qualification",
    bullets: [
      "Asks the right discovery questions for every campaign.",
      "Routes hot prospects to sales instantly with a live alert.",
      "Filters tire-kickers so you focus on revenue conversations.",
    ],
  },
  {
    icon: "🔗",
    title: "391% Higher Conversion Rates ²",
    bullets: [
      "Responding within one minute instead of waiting five minutes yields a 391% lift in conversions",
      "Logs every phone conversation against the originating ad.",
      "Source:\n↗\nLead Angel",
    ],
  },
];

const useCases: UseCase[] = [
  {
    title: "Cemeteries & Memorial Parks",
    narrative:
      "Families expect a compassionate, immediate response when they reach out. Our after-hours cemetery answering service gently triages urgent arrangements, books cemetery tours and consultation appointments, and relays details to your staff without missing a call.",
    keyword: "cemetery answering service",
  },
  {
    title: "Pre-Need Planning",
    narrative:
      "Planning ahead brings peace of mind. Our AI Family Service Phone Assistant™ qualifies pre-need prospects, schedules consultation appointments at the cemetery office, and sends follow-up materials about burial plots, mausoleums, and memorial options.",
    keyword: "cemetery pre-need planning",
  },
  {
    title: "Immediate Need Services",
    narrative:
      "When a family faces a loss, they need immediate support. Our AI Family Service Phone Assistant™ handles urgent calls with empathy, gathers essential details, and instantly notifies your staff so you can focus on serving families without delay.",
    keyword: "cemetery immediate need services",
  },
  {
    title: "Cemetery Tours & Consultations",
    narrative:
      "Prospective families want to see available options. Our AI schedules cemetery tours, answers questions about plot locations, pricing, and memorial features, and books appointments that fit your team's availability.",
    keyword: "cemetery tour scheduling",
  },
  {
    title: "Memorial Products & Services",
    narrative:
      "From headstones to cremation niches, families have questions. Our AI chatbot and phone receptionist answer questions about memorial products, send pricing information, and qualify leads for your sales team to follow up.",
    keyword: "memorial products information",
  },
];

const faqs: FaqItem[] = [
  {
    question: "Will it sound robotic?",
    answer:
      "Not at all. Our AI uses natural conversation flow trained on thousands of real business calls. Most callers think they are speaking with a seasoned receptionist—listen to the live demo above.",
  },
  {
    question: "What if it can't answer a question?",
    answer:
      "The AI Family Service Phone Assistant™ resolves common FAQs. For edge cases it warm-transfers to your team or captures a detailed message with automated callback scheduling.",
  },
  {
    question: "How long does setup take?",
    answer:
      "Most teams launch within 10 minutes. Just connect your calendar, choose your scripts, and tell the AI how to route hot leads.",
  },
  {
    question: "Does it integrate with my tools?",
    answer:
      "Yes. We integrate with Google Calendar, Outlook, Salesforce, HubSpot, and 50+ other platforms. That includes CRM integration with your phone system and direct Google Ads and Facebook Lead Ads sync.",
  },
  {
    question: "What if I'm already using a call service?",
    answer:
      "Traditional answering services take messages. The AI Family Service Phone Assistant™ and AI Chatbot qualify leads, book appointments, and respond instantly—the crucial difference between virtual receptionist vs AI Family Service Phone Assistant™ performance.",
  },
];

const cemeteryFaqs: FaqItem[] = [
  {
    question: "Can an AI receptionist answer after-hours death calls?",
    answer: "Yes. A cemetery or funeral AI receptionist can answer after-hours calls, collect the caller's name, phone number, location, and immediate need, then notify the appropriate staff member based on your call-handling rules."
  },
  {
    question: "Can AI help families asking about burial arrangements?",
    answer: "Yes. The AI receptionist can help families by gathering basic information about burial needs, preferred cemetery location, timeline, and whether they are asking about an immediate need or future planning."
  },
  {
    question: "Can an AI receptionist handle funeral service inquiries?",
    answer: "Yes. The AI can answer common funeral service inquiries, collect details from the caller, and route the request to the correct funeral home or cemetery team member for follow-up."
  },
  {
    question: "Can AI distinguish between at-need and pre-need callers?",
    answer: "Yes. The AI receptionist can ask whether the caller needs immediate assistance because a death has occurred or whether they are planning in advance. This helps your team prioritize urgent at-need calls while still capturing pre-need opportunities."
  },
  {
    question: "Can AI collect information for a first call transfer?",
    answer: "Yes. The AI can collect important first-call details such as the caller's name, phone number, relationship to the deceased, location of the deceased, time of passing, and whether immediate transportation or staff contact is needed."
  },
  {
    question: "Can AI answer questions about cemetery property availability?",
    answer: "Yes. The AI receptionist can collect inquiries about burial plots, mausoleum spaces, cremation niches, family estates, and other cemetery property options, then send the details to your sales or family service team."
  },
  {
    question: "Can AI help families with cremation and burial questions?",
    answer: "Yes. The AI can collect basic questions about cremation, burial, memorialization, placement options, and next steps. For sensitive or complex questions, it can route the caller to a trained staff member."
  },
  {
    question: "Can AI provide directions and visiting hour information?",
    answer: "Yes. The AI receptionist can provide basic cemetery or funeral home information such as location, directions, visiting hours, office hours, service locations, and general contact details when configured with that information."
  },
  {
    question: "Can AI notify staff about urgent family calls?",
    answer: "Yes. The AI can identify urgent family calls and send an alert, call summary, or transfer request to the appropriate staff member. This helps ensure sensitive calls are not delayed or missed."
  },
  {
    question: "Can an AI receptionist support grieving families respectfully?",
    answer: "Yes. A cemetery or funeral AI receptionist can be scripted with calm, respectful, and compassionate language. Its role is to answer promptly, gather needed information, and connect the family with the right person as quickly as possible."
  }
];

const comparisonRows: ComparisonRow[] = [
  {
    feature: "Response Time",
    youware: "< 10 seconds",
    human: "2-48 hours",
    voicemail: "Never",
  },
  {
    feature: "Availability",
    youware: "24/7/365",
    human: "Business hours only",
    voicemail: "24/7 (no response)",
  },
  {
    feature: "Cost",
    youware: "Starting @ $ 199/month",
    human: "$3,000+/month",
    voicemail: "Free (but loses most leads)",
  },
  {
    feature: "Appointment Booking",
    youware: "Automatic",
    human: "Manual",
    voicemail: "No",
  },
  {
    feature: "Lead Qualification",
    youware: "Yes",
    human: "Inconsistent",
    voicemail: "No",
  },
  {
    feature: "Scalability",
    youware: "Unlimited calls and chats",
    human: "Limited capacity",
    voicemail: "Unlimited (but useless)",
  },
];

const trustBadges = [
  "⭐ 4.9/5 Stars (500+ Reviews)",
  "🔒 HIPAA Compliant",
  "📱 Works with 50+ CRM Platforms",
  "🏆 Featured in Ad & Martech Publications",
  "🛡️ Enterprise-Grade Encryption",
];

const integrationLogos = [
  "Google Ads",
  "Facebook Lead Ads",
  "LinkedIn",
];

const testimonials: Testimonial[] = [
  {
    name: "Robert K.",
    role: "Cemetery Director",
    quote:
      "We were missing family calls after hours, especially for immediate needs. Since adding the AI Family Service Phone Assistant™, every call gets answered with compassion, and we never miss a potential pre-need lead again.",
    image:
      "https://public.youware.com/users-website-assets/prod/ae526346-979d-4db0-a172-6d74837c4f94/fbc2616b63bb450aa6e414d5e58c4bfa",
  },
  {
    name: "Patricia M.",
    role: "Memorial Park Manager",
    quote:
      "Our staff was overwhelmed with tour requests and pricing questions. The AI handles all the initial inquiries and schedules cemetery visits. We've doubled our pre-need consultations.",
    image:
      "https://public.youware.com/users-website-assets/prod/ae526346-979d-4db0-a172-6d74837c4f94/5b7a4dc5382e4b9e9eeeac319a867fa5",
  },
  {
    name: "James L.",
    role: "Cemetery Operations Director",
    quote:
      "Families calling for immediate arrangements need quick responses. The AI Assistant captures all the details and alerts our team instantly. It's made a difficult process smoother for everyone.",
    image:
      "https://public.youware.com/users-website-assets/prod/ae526346-979d-4db0-a172-6d74837c4f94/ea55a902ff0646f29f97096683d49819",
  },
];

const workflowSteps: WorkflowStep[] = [
  {
    icon: "⚙️",
    title: "Custom Setup",
    bullets: [
      "Connect your calendar and phone number.",
      "Customize AI scripts to mirror your brand voice.",
      "Set routing rules to fast-track hot inbound call leads.",
    ],
  },
  {
    icon: "📞",
    title: "AI Answers Every Call",
    bullets: [
      "Greets callers by name and originating ad.",
      "Answers FAQs, captures details, and qualifies buyers.",
      "Logs call transcripts in your CRM instantly.",
    ],
  },
  {
    icon: "📈",
    title: "Watch Conversions Soar",
    bullets: [
      "Hot leads routed instantly to your team.",
      "Analytics reveal which ads convert fastest.",
      "Pinpoint where follow-up lags so you can recover more revenue.",
    ],
  },
];

const metrics: Metric[] = [
  {
    value: 100,
    suffix: "x",
    caption: "Faster lead connections",
    sourceLabel: "MIT Lead Response Study",
    sourceUrl: "https://cdn2.hubspot.net/hub/25649/file-13535879-pdf/docs/mit_study.pdf",
    footnote: "⁵",
  },
  {
    value: 78,
    suffix: "%",
    caption: "Buyers choose first responder",
    sourceLabel: "Chili Piper Speed to Lead Statistics",
    sourceUrl: "https://www.chilipiper.com/article/speed-to-lead-statistics",
    footnote: "⁶",
  },
  {
    value: 47,
    suffix: " hrs",
    caption: "Average response delay",
    sourceLabel: "leadport.app",
    sourceUrl: "https://www.leadport.app/en/blog/key-lead-response-time-statistics/?utm_source=chatgpt.com",
    footnote: "³",
  },
  {
    value: 73,
    suffix: "%",
    caption: "Leads left unanswered",
    sourceLabel: "CallTools",
    sourceUrl: "https://calltools.com/blog/6-strategies-to-improve-speed-to-lead-conversion-time/?utm_source=chatgpt.com",
    footnote: "⁴",
  },
];

const conversionBenchmarks: Record<ResponseTimeKey, number> = {
  "Within 5 Minutes": 0.35,
  "30 Minutes": 0.18,
  "1 Hour": 0.12,
  "24+ Hours": 0.05,
};

const CHAT_STORAGE_KEY = "black-swan-consulting-faq-demo-chat-history";
const ASSISTANT_NAME = "AI Chatbot Demo";
const CHAT_SCENE = "faq_demo_assistant";

const quickReplies = [
  "Help me book a demo",
];


const createTimestamp = () => new Date().toISOString();

const createInitialChatHistory = (): ChatMessage[] => [
  {
    from: "ai",
    text: "Hi! I'm Black Swan Consulting's AI Family Service Phone Assistant™ and AI Chatbot. Ask me anything about coverage, pricing, or integrations—or say \"Help me book a demo\" and I'll provide the link.",
    timestamp: createTimestamp(),
  },
];

const formatChatTimestamp = (isoString: string): string => {
  try {
    return new Date(isoString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (error) {
    return "";
  }
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(value)));

const formatCurrencyInput = (value: string) => {
  if (!value) return "";
  if (value === "0") return "$0";

  const numericValue = Number(value.replace(/[^0-9.]/g, ""));
  if (Number.isNaN(numericValue)) return "";
  if (numericValue === 0) return "$0";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(numericValue)));
};

const logInputs = (inputs: RoiInputs) => {
  console.debug("ROI inputs updated", inputs);
};

const CountUp: React.FC<{ target: number; prefix?: string; suffix?: string }> = ({
  target,
  prefix = "",
  suffix = "",
}) => {
  const [value, setValue] = useState(0);
  const elementRef = useRef<HTMLSpanElement | null>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const node = elementRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const start = performance.now();
          const duration = 1200;

          const animate = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            setValue(target * progress);
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [target]);

  const displayValue = Number.isInteger(target)
    ? Math.round(value).toLocaleString()
    : value.toFixed(1);

  return (
    <span ref={elementRef} className="font-semibold text-blue-600">
      {prefix}
      {displayValue}
      {suffix}
    </span>
  );
};

const AccordionItem: React.FC<{ item: FaqItem; isOpen: boolean; onToggle: () => void }> = ({
  item,
  isOpen,
  onToggle,
}) => (
  <div className="rounded-3xl border border-slate-200 bg-white">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left"
    >
      <span className="text-base font-semibold text-slate-900">{item.question}</span>
      <span className="text-xl text-slate-400">{isOpen ? "−" : "+"}</span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="overflow-hidden px-6 pb-6 text-sm text-slate-600"
        >
          {item.answer}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const ComparisonTable = ({ rows }: { rows: ComparisonRow[] }) => (
  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-blue-100">
    <table className="w-full border-collapse text-sm">
      <thead className="bg-slate-900 text-white">
        <tr>
          <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-200">
            Feature
          </th>
          <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">AI Family Service Phone Assistant™ and AI Chatbot</th>
          <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Human Receptionist</th>
          <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Voicemail</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.feature} className="odd:bg-slate-50">
            <td className="px-6 py-4 text-slate-700">{row.feature}</td>
            <td className="px-6 py-4 font-semibold text-blue-600">{row.youware}</td>
            <td className="px-6 py-4 text-slate-600">{row.human}</td>
            <td className="px-6 py-4 text-slate-500">{row.voicemail}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function App() {
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const exitTriggeredRef = useRef(false);
  const [activeUseCase, setActiveUseCase] = useState<UseCase>(useCases[0]);
  const [inputs, setInputs] = useState<RoiInputs>({
    adSpend: "12000",
    leads: "140",
    saleValue: "1800",
    responseTime: "24+ Hours",
  });

  const formattedInputs = useMemo(
    () => ({
      adSpend: formatCurrencyInput(inputs.adSpend),
      saleValue: formatCurrencyInput(inputs.saleValue),
    }),
    [inputs.adSpend, inputs.saleValue],
  );
  const [openFaq, setOpenFaq] = useState<string | null>(faqs[0].question);
  const [openNewFaq, setOpenNewFaq] = useState<number | null>(null);
  const [leadForm, setLeadForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    optIn: false,
  });

  const [formError, setFormError] = useState<string | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "AI Family Service Phone Assistant™ and AI Chatbot - Never Miss a Lead";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Your inbound calls aren't converting because follow-up is too slow. The AI Family Service Phone Assistant™ and AI Chatbot answer in seconds, book appointments automatically, and increase conversions by 47%. Start your free trial."
      );
    }


  }, []);


  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      if (event.clientY <= 0 && !exitTriggeredRef.current) {
        exitTriggeredRef.current = true;
        // setExitModalOpen(true);
      }
    };

    const fallbackTimer = setTimeout(() => {
      if (!exitTriggeredRef.current) {
        exitTriggeredRef.current = true;
        // setExitModalOpen(true);
      }
    }, 22000);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setExitModalOpen(false);
        setChatOpen(false);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(fallbackTimer);
    };
  }, []);

  const roi = useMemo(() => {
    logInputs(inputs);
    const adSpend = Number(inputs.adSpend) || 0;
    const leads = Number(inputs.leads) || 0;
    const saleValue = Number(inputs.saleValue) || 0;
    const currentRate = conversionBenchmarks[inputs.responseTime];
    const bestRate = conversionBenchmarks["Within 5 Minutes"];

    const currentDeals = leads * currentRate;
    const maxDeals = leads * bestRate;
    const lostDeals = Math.max(0, maxDeals - currentDeals);
    const lostRevenue = Math.max(0, lostDeals * saleValue);
    const recoveredRevenue = lostRevenue;
    const improvedROIPercent = adSpend > 0 ? Math.round((recoveredRevenue / adSpend) * 100) : 0;

    return {
      adSpend,
      leads,
      saleValue,
      currentDeals,
      maxDeals,
      lostDeals,
      lostRevenue,
      recoveredRevenue,
      improvedROIPercent,
    };
  }, [inputs]);

  const updateInput = (field: keyof RoiInputs) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value } = event.target;

      if (field === "responseTime") {
        setInputs((prev) => ({
          ...prev,
          responseTime: value as ResponseTimeKey,
        }));
        return;
      }

      const normalized = value.replace(/[^0-9.]/g, "");
      const sanitized = normalized === "" ? "" : normalized;

      setInputs((prev) => ({
        ...prev,
        [field]: sanitized,
      }));
    };

  const handleLeadChange = (field: keyof typeof leadForm) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value, type, checked } = event.target;
      const newValue = type === "checkbox" ? checked : value;
      setLeadForm((prev) => ({ ...prev, [field]: newValue }));
    };

  const handleLeadSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!leadForm.name || !leadForm.email || !leadForm.phone) {
      setFormError("Please add your name, email, and phone so we can reserve your demo.");
      return;
    }

    if (!leadForm.optIn) {
      setFormError("Please agree to the messaging terms to proceed.");
      return;
    }

    const formData = new URLSearchParams({
      name: leadForm.name,
      email: leadForm.email,
      phone: leadForm.phone,
      company: leadForm.company,
    });

    window.location.href = `mailto:inquiries@blackswan-consulting.com?subject=New%20Consultation%20Request&body=${encodeURIComponent(
      `Name: ${leadForm.name}\nEmail: ${leadForm.email}\nPhone: ${leadForm.phone}\nCompany: ${leadForm.company}`
    )}`;

    setFormError(null);
    setFormSubmitted(true);
    setLeadForm({ name: "", email: "", phone: "", company: "" });
  };


  const handleExitClose = () => setExitModalOpen(false);

  const scrollToSection = (selector: string) => {
    const target = document.querySelector(selector);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };



  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" role="document">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur" aria-label="Primary">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3" role="banner">
            <img
              src={logoEclipseSwan}
              alt="AI Family Service Phone Assistant™ and AI Chatbot logo"
              className="h-10 w-10 rounded-full object-cover"
            />
            <div>
              <p className="text-xs font-semibold text-slate-500">Black Swan Consulting</p>
              <h1 className="text-base font-semibold text-slate-900">AI Family Service Phone Assistant™ and AI Chatbot for Cemeteries</h1>
            </div>
          </div>
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 lg:flex" aria-label="Main navigation">
            {[
              { label: "Problem", target: "#problem" },
              { label: "Solution", target: "#solution" },
              { label: "FAQ", target: "#faq" },
            ].map((link) => (
              <button
                key={link.target}
                type="button"
                className="hover:text-blue-600"
                onClick={() => scrollToSection(link.target)}
              >
                {link.label}
              </button>
            ))}
          </nav>
          <div className="relative flex items-center gap-4">
            <a
              className="hidden items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 md:inline-flex"
              href="tel:844-622-9007"
              style={{
                textAlign: "center"
              }}>
              📞 Click to Call
            </a>
            <HamburgerButton
              className="lg:hidden"
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-controls="mobile-nav-menu"
            />
            <button
              type="button"
              onClick={() => scrollToSection("#cta")}
              className="hidden items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-blue-500/30 transition hover:shadow-lg lg:inline-flex"
            >
              Start a 30-Day Free Trial
            </button>
            <AnimatePresence>
              {isMobileMenuOpen && (
                <motion.div
                  id="mobile-nav-menu"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-14 w-56 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-slate-900/10 lg:hidden"
                >
                  <div className="flex flex-col gap-2">
                    {[
                      { label: "Problem", target: "#problem" },
                      { label: "Solution", target: "#solution" },
                      { label: "FAQ", target: "#faq" },
                    ].map((link) => (
                      <button
                        key={`mobile-${link.target}`}
                        type="button"
                        className="rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-600 transition hover:bg-slate-100"
                        onClick={() => {
                          scrollToSection(link.target);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {link.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-blue-500/30 transition hover:shadow-lg"
                      onClick={() => {
                        scrollToSection("#cta");
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Start a 30-Day Free Trial
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>
      <main className="overflow-hidden">
        <motion.section
          id="hero"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8 text-white"
        >
          <h1 className="sr-only">AI Receptionist for Cemeteries and Memorial Parks</h1>
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.22),_transparent_55%)]"
            aria-hidden="true"
            role="presentation"
          />
          <div className="relative mx-auto max-w-6xl px-6">
            {/* Eyebrow */}
            <p className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-emerald-300">
              WHEN FAMILIES CALL, EVERY MINUTE MATTERS. ANSWER THE FIRST TIME.
            </p>

            {/* Headline */}
            <h2 className="mt-6 text-center text-4xl font-semibold leading-tight sm:text-5xl">
              A 24/7 AI Receptionist Built for Cemeteries and Memorial Parks
            </h2>

            {/* Subheadline */}
            <p className="mx-auto mt-6 max-w-2xl text-center text-lg text-slate-200">
              Greet families with warmth, answer common questions, capture pre-need inquiries, and help schedule tours the moment a call or website visit comes in—without sending grieving families to voicemail or leaving them waiting on a contact form.
            </p>

            {/* Trust box */}
            <div className="mx-auto mt-8 max-w-2xl rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-center text-sm text-slate-200">
                <span className="font-semibold text-emerald-300">78%</span>of customers buy from the first responder.
                                
              </p>
            </div>

            {/* Pills */}
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <span className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-slate-200">
                Built specifically for cemeteries, memorial parks, and funeral-adjacent providers
              </span>
              <span className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-slate-200">Talk to Lucy now—tap the blue phone icon to experience a live demo</span>
            </div>

            {/* Primary CTA */}
            <div className="mt-8 flex justify-center">
              <a
                href="https://calendly.com/meeting-blackswan-consulting/meeting-ai-family-service-phone-assistant"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-8 py-4 text-base font-semibold text-white transition hover:bg-blue-500"
                aria-label="Schedule a 15-Minute Demo"
              >Book an Ai Family Services Phone Assistant setup Call NOW!</a>
            </div>

            {/* Trust layer */}
            <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-slate-300">
              Handles after-hours calls, tour requests, pricing questions, pre-need inquiries, and urgent family messages—with human handoff when needed.
            </p>
          </div>
        </motion.section>

        <motion.section
          id="problem"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="py-8"
        >
          <div className="mx-auto max-w-5xl px-6">
            <div className="mx-auto max-w-3xl text-center">
              <h3 className="text-3xl font-semibold text-slate-900">Why Your Leads Aren't Converting</h3>
              <p className="mt-4 text-lg text-slate-600">The leads are there, but by the time you call back, they've chosen the competitor who responded first. Our AI Family Service Phone Assistant™ keeps hot leads engaged while the AI chatbot continues the conversation before your team calls back.</p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              {problemStats.map((item) => (
                <div
                  key={item.label}
                  className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm shadow-slate-200/40"
                >
                  <p className="text-2xl font-semibold text-slate-900">
                    <CountUp target={item.value} suffix={item.suffix ?? ""} />
                  </p>
                  <p className="mt-2 text-lg font-semibold text-slate-800">
                    {item.label}{" "}
                    <sup className="align-super text-sm text-blue-600">{item.footnote}</sup>
                  </p>
                  <p className="mt-3 text-sm text-slate-600">{item.description}</p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500">
                    <span className="font-semibold text-slate-600">Source:</span>
                    <a
                      href={
                        item.sourceLabel === "Lead Angel"
                          ? "https://www.leadangel.com/blog/operations/speed-to-lead-statistics/"
                          : item.sourceUrl
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-semibold text-blue-600 hover:text-blue-500"
                    >
                      <span aria-hidden="true">↗</span>
                      <span className="underline decoration-dotted underline-offset-2">
                        {item.sourceLabel}
                      </span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.section>
        <motion.section
          id="demo-audio"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="py-8"
        >

        </motion.section>
        <motion.section
          id="solution"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-slate-900 py-8 text-white"
        >
          <div className="mx-auto max-w-6xl px-6">
            <div className="max-w-xl">
              <div
                id="about"
                className="mb-4 block text-xs font-semibold uppercase tracking-widest text-emerald-300"
                style={{
                  fontSize: "20px"
                }}
              >
                About Us
              </div>
              <h3 className="text-3xl font-semibold">Instant Response. Automatic Scheduling. More Conversions.</h3>
              <p className="mt-4 text-slate-300">Meet your 24/7 AI Family Service Phone Assistant™ and AI chatbot. The AI Family Service Phone Assistant™ answers every inbound call with brand-perfect scripting while the AI Chatbot continues the nurture on web chat—together the AI Family Service Phone Assistant™ and AI Chatbot deliver the fastest way to improve lead response time, automate appointment scheduling for leads, and guarantee 24/7 call answering for small business advertisers without adding headcount.</p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {featureGrid.map((feature) => (
                <div key={feature.title} className="flex h-full flex-col rounded-3xl border border-white/10 bg-white/5 p-6">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-2xl">
                    {feature.icon}
                  </div>
                  <p className="mt-4 text-xl font-semibold">{feature.title}</p>
                  <ul className="mt-4 space-y-3 text-sm text-slate-200">
                    {feature.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2">
                        <span className="mt-1 text-emerald-300">•</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </motion.section>
        <motion.section
          id="industries"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="py-8"
        >
          <div className="mx-auto max-w-6xl px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-3xl font-semibold text-slate-900">Cemetery Industry Solutions</h3>
              <p className="mt-4 text-slate-600">
                See how the AI Family Service Phone Assistant™ and AI Chatbot help cemeteries capture every lead, schedule cemetery tours, and provide compassionate support to families—regardless of channel or time of day.
              </p>
            </div>
            <div className="mt-10 grid gap-8 lg:grid-cols-[0.45fr_1fr]">
              <div className="flex flex-col gap-3">
                {useCases.map((item) => {
                  const isActive = activeUseCase.title === item.title;
                  return (
                    <button
                      key={item.title}
                      type="button"
                      onClick={() => setActiveUseCase(item)}
                      className={`rounded-2xl border px-5 py-4 text-left text-sm font-semibold transition ${
                        isActive
                          ? "border-blue-500 bg-blue-50 text-blue-700 shadow"
                          : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-blue-600"
                      }`}
                    >
                      <span className="flex items-center justify-between gap-3">
                        {item.title}
                        <span className="text-xs font-semibold uppercase tracking-wide text-blue-400">
                          View AI Family Service Phone Assistant™ + AI Chatbot detail
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-blue-100">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-blue-500">{activeUseCase.keyword} powered by AI Family Service Phone Assistant™ and AI Chatbot</p>
                    <h4 className="mt-2 text-2xl font-semibold text-slate-900">{activeUseCase.title}: AI Family Service Phone Assistant™ + AI Chatbot</h4>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-600">
                    Vital leads
                  </span>
                </div>
                <p className="mt-4 text-slate-600">{activeUseCase.narrative}</p>
                <div className="mt-6 grid gap-4 text-sm text-slate-500 md:grid-cols-2">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="font-semibold text-slate-700">AI handles</p>
                    <p className="mt-2 text-sm text-slate-600">
                      Lead capture, qualification, calendar booking with smart reminders, and detailed CRM analytics—precisely what our AI Family Service Phone Assistant™ and AI chatbot tag-team around the clock.
                    </p>
                    <ul className="mt-3 space-y-1 text-xs text-slate-500">
                      <li>• Compassionate intake scripts</li>
                      <li>• 24/7 availability for urgent calls</li>
                      <li>• Automatic voice and text follow-up</li>
                    </ul>
                  </div>
                  <div className="rounded-2xl bg-blue-50 p-4">
                    <p className="font-semibold text-blue-700">You focus on</p>
                    <p className="mt-2 text-sm text-blue-600">
                      Closing the deal, delivering premium service, and scaling ad campaigns with confidence.
                    </p>
                    <ul className="mt-3 space-y-1 text-xs text-blue-500">
                      <li>• Personalized consultations</li>
                      <li>• On-site experiences</li>
                      <li>• Community relationships</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-6 grid gap-3 text-xs text-slate-500 md:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="font-semibold text-slate-700">Opportunities</p>
                    <p className="mt-2">
                      Pre-need inquiries, immediate arrangements, and memorial planning handled with empathy.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="font-semibold text-slate-700">Key data captured</p>
                    <p className="mt-2">
                      Family details, service preferences, scheduling availability, and budget considerations.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="font-semibold text-slate-700">Next steps</p>
                    <p className="mt-2">
                      Notify Key People instantly, sync notes to CRM, and trigger grief-support email sequences.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>
        <motion.section
          id="workflow"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-slate-900 py-8 text-white"
        >
          <div className="mx-auto max-w-6xl px-6">
            <h3 className="text-3xl font-semibold">How It Works in 3 Simple Steps</h3>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {workflowSteps.map((step) => (
                <div key={step.title} className="flex h-full flex-col rounded-3xl border border-white/10 bg-white/10 p-6">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-2xl">
                    {step.icon}
                  </div>
                  <p className="mt-4 text-xl font-semibold">{step.title}</p>
                  <ul className="mt-4 space-y-2 text-sm text-slate-200">
                    {step.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2">
                        <span className="mt-1 text-emerald-300">•</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </motion.section>
        <motion.section
          id="testimonials"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="py-8"
        >
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-3xl font-semibold text-slate-900">Social Proof That Converts</h3>
                <p className="mt-3 text-slate-600">
                  Advertisers who install the AI Family Service Phone Assistant™ and AI Chatbot recover leads that would have gone cold—the AI Family Service Phone Assistant™ delights callers, the AI Chatbot nurtures every form fill, and customers rave about the conversion surge.
                </p>
              </div>

            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.name}
                  className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="flex items-center gap-4">

                    <div>
                      <p className="text-sm font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-xs text-slate-500">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-600">“{testimonial.quote}”</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>
        <motion.section
          id="comparison"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-slate-900 py-8"
        >
          <div className="mx-auto max-w-6xl px-6 text-white">
            <div className="max-w-3xl">
              <h3
                className="text-3xl font-semibold"
                style={{
                  fontSize: "28px"
                }}>AI Family Service Phone Assistant™ and AI Chatbot vs. Traditional Solutions</h3>
              <p className="mt-4 text-slate-300">
                Affordable answering service for small business teams who can't afford a full-time receptionist but can't lose another paid lead.
              </p>
            </div>
            <div className="mt-10">
              <ComparisonTable rows={comparisonRows} />
            </div>
          </div>
        </motion.section>
        <motion.section
          id="faq"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="py-8"
        >

        </motion.section>

        {/* Cemetery and Funeral AI Receptionist FAQ Section */}
        <motion.section
          id="cemetery-faq"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-slate-50 py-16"
        >
          <div className="mx-auto max-w-4xl px-6">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-semibold text-slate-900">Cemetery and Funeral AI Receptionist FAQ</h2>
              <p className="mt-4 text-slate-600">
                Common questions about AI receptionists for cemeteries and funeral homes.
              </p>
            </div>
            <div className="grid gap-6">
              {cemeteryFaqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* New FAQ Section */}
        <motion.section
          id="faq"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-slate-50 py-16"
        >

        </motion.section>

        <motion.section
          id="cta"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative overflow-hidden bg-slate-900 py-8 text-white"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,_rgba(16,185,129,0.35),_transparent_60%)]" aria-hidden="true" role="presentation" />
          <div className="relative mx-auto max-w-4xl px-6 text-center">
            <div className="space-y-6">
              <h3 className="text-4xl font-semibold">Stop Wasting Leads You'll Never Convert</h3>
              <p className="text-slate-200">
                Join 2,500+ businesses using AI to respond instantly, book more appointments, and maximize ROI from their advertising.
              </p>
              <ul className="space-y-3 text-sm text-slate-200">
                <li>✅ 30-Day free trial</li>
                <li>✅ No credit card required</li>
                <li>✅ Cancel anytime</li>

              </ul>
              <p className="text-sm text-emerald-200">
                Businesses using the AI Family Service Phone Assistant™ and AI Chatbot see consistent speed-to-lead performance and more booked consultations.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-200">
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <a
                    href="tel:844-622-9007"
                    className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500"
                  >
                    📞 Click to Call
                  </a>

                </div>
                <a
                  href="https://calendly.com/meeting-blackswan-consulting/meeting-ai-family-service-phone-assistant"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-8 py-4 text-base font-semibold text-white transition hover:bg-blue-500"
                  aria-label="Schedule a 15-Minute Demo"
                >
                  Book an Ai Family Services Phone Assistant setup Call NOW!
                </a>
              </div>
            </div>
          </div>
        </motion.section>
      </main>
      <footer className="bg-slate-950 py-8 text-slate-300">
        {/* About Us section anchor removed from here */}
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-12 lg:grid-cols-4">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img
                  src={logoEclipseSwan}
                  alt="AI Family Service Phone Assistant™ and AI Chatbot logo"
                  className="h-10 w-10 rounded-full object-cover"
                />
                <span className="text-lg font-semibold text-white">AI Family Service Phone Assistant™</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">
                Never miss a lead again. Our AI Family Service Phone Assistant™ and AI Chatbot answer instantly, qualify prospects, and book appointments 24/7.
              </p>
              <div className="flex gap-4">
                {/* Social icons could go here */}
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Product</h3>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href="#problem" className="hover:text-blue-400 transition">Problem</a></li>
                <li><a href="#solution" className="hover:text-blue-400 transition">Solution</a></li>
                <li></li>
                <li></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Company</h3>
              <ul className="space-y-3 text-sm text-slate-400">
                <li>
                  <a
                    href="#about"
                    className="hover:text-blue-400 transition"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection("#about");
                    }}
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a
                    href="https://blackswan-consulting.com/cemetery-managers-blog/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-blue-400 transition"
                  >
                    Cemetery Manager's Blog
                  </a>
                </li>
                <li></li>
                <li></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Contact</h3>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex items-start gap-3">


                </li>
                <li className="flex items-center gap-3">
                  <span className="text-lg">📞</span>
                  <a href="tel:844-622-9007" className="hover:text-blue-400 transition">844-622-9007</a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-lg">✉️</span>
                  <a href="mailto:info@blackswan-consulting.com" className="hover:text-blue-400 transition">info@blackswan-consulting.com</a>
                </li>
              </ul>
            </div>
          </div>

          <p
            className="mt-16 text-xs text-slate-600 max-w-3xl mx-auto"
            style={{
              fontSize: "15px",
              textAlign: "center"
            }}>
            {/* Disclaimer text */}
            Disclaimer: All statistics cited are from third-party research studies. Individual results may vary based on industry, implementation, and other factors. Past performance does not guarantee future results.
          </p>

          <div className="mt-8 border-t border-slate-800 pt-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <p className="text-xs text-slate-500">
                © 2025 AI Family Service Phone Assistant™ and AI Chatbot. A division of Black Swan Consulting LLC. All rights reserved.
              </p>
              <div className="flex gap-6 text-xs text-slate-500">
                <LegalLinks />
              </div>
            </div>
          </div>
        </div>
      </footer>
      <AnimatePresence>
        {exitModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-xl rounded-3xl bg-white p-8 shadow-2xl"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900">Before You Bounce…</h3>
                  <p className="mt-2 text-sm text-slate-600">Hear a live AI Family Service Phone Assistant™ demo Call and see how advertisers cut response time from hours to seconds—without adding headcount.</p>
                </div>
                <button
                  type="button"
                  className="text-slate-400 transition hover:text-slate-600"
                  onClick={handleExitClose}
                  aria-label="Close exit intent modal"
                >
                  ✕
                </button>
              </div>
              <div className="mt-6 flex flex-wrap gap-3 text-xs text-slate-500">
                <span className="rounded-full bg-slate-100 px-3 py-1">why aren't my ads converting</span>
                <span className="rounded-full bg-slate-100 px-3 py-1">reduce cost per acquisition</span>
                <span className="rounded-full bg-slate-100 px-3 py-1">leads going cold after ad click</span>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    scrollToSection("#demo-audio");
                    handleExitClose();
                  }}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:bg-blue-500"
                >
                  ▶ Hear Live Demo
                </button>
                <button
                  type="button"
                  onClick={() => {
                    scrollToSection("#cta");
                    handleExitClose();
                  }}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-600"
                >
                  Start a 30-Day Free Trial
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
